import React, { FC, memo, useCallback, useEffect, useState, Suspense, useMemo } from 'react';
import styles from './product-details.module.css';

import {
    M130ProductDetailModule,
    M131ProductDetailModule,
    ProductViewModel,
} from '~/lib/data-contract';
import qs from 'qs';
import { RawHtml, Modal } from '~/shared/components';
import ColorSelector from '../color-selector/color-selector.component';
import { useAlgoliaAnalytics, useNotification } from '~/shared/hooks';
import { useTranslation } from '~/shared/utils';
import { useBasketAdd } from '~/features/basket';
import Easysize from '../easy-size/easy-size.component';
import Actions from '../actions/actions.component';
import ReserveContainer from '~/features/Reserve/ReserveContainer';
import { SizeSelector } from '~/shared/components/size-selector/size-selector.component';
import ProductDetailsSharedLayout from '../layout/shared-layout.component';

type Props = (M130ProductDetailModule | M131ProductDetailModule) & {
    product: ProductViewModel;
} ;

const ProductDetails: FC<Props> = memo(({ sizeGuideContent, product }) => {
    const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
    const [sizeError, setSizeError] = useState<boolean>(false);
    const [isReserveActive, setIsReserveActive] = useState(false);

    const { trackAddToBasket } = useAlgoliaAnalytics();
    const { translate } = useTranslation();
    const { push } = useNotification();
    const { add } = useBasketAdd();

    const {
        images,
        accordions,
        name,
        subtitle,
        priceDetails,
        colors,
        sizes,
        productColorId: productModelColorId,
        imageTags,
        sizingText,
    } = product as ProductViewModel;

    const addToBasket = useCallback(() => {
        if (!selectedVariantId) {
            setSizeError(true);
            return;
        }

        if (!productModelColorId) {
            push({ severity: 'warning', text: translate('message.fatalError') });
            return;
        }

        const { hash } = window.location;
        const params = qs.parse(hash.slice(1));
        if (params.queryId) {
            trackAddToBasket([productModelColorId.toString()], params.queryId.toString());
        }
        add({
            productId: productModelColorId,
            variantId: selectedVariantId,
            quantity: 1,
            pageViewId: window.EasySizeParametersDebug?.easysize?.pageview_id || '',
            userId: window.EasySizeParametersDebug?.easysize?.user_id || '',
        });
    }, [add, productModelColorId, push, trackAddToBasket, translate, selectedVariantId]);

    const handleReserve = useCallback(() => {
        if (!selectedVariantId) {
            setSizeError(true);
            return;
        }
        setIsReserveActive(true);
    }, [selectedVariantId]);

    // Memoize size selection handler
    const handleSizeSelect = useCallback((val: string) => {
        setSelectedVariantId(val);
        setSizeError(false);
    }, []);

    // Memoize modal close handler
    const handleModalClose = useCallback(() => setIsReserveActive(false), []);

    // Memoize product data object to prevent unnecessary re-renders
    const productData = useMemo(() => ({
        images,
        accordions,
        title: name,
        subtitle,
        priceDetails,
        imageTags,
    }), [images, accordions, name, subtitle, priceDetails, imageTags]);

    // Memoize EasySize callback to prevent re-renders
    const handleSizeFound = useCallback((size: string) => {
        setSelectedVariantId(size);
    }, []);

    // Memoize modal headline to prevent unnecessary re-computations
    const modalHeadline = useMemo(() => translate('reservation.overlayHeading'), [translate]);

    useEffect(() => {
        // Reset variant id on product change and track new product
        setSelectedVariantId(null);
    }, [product?.productColorId]);

    useEffect(() => {
        if (sizes?.length === 1 && !selectedVariantId) {
            setSelectedVariantId(sizes[0].variantId || null);
        }
    }, [selectedVariantId, sizes]);

    // We want to make sure the og:image is exactly the same as presented in the Structured Data, so here we go!
    // const resizedFirstProductImage = addResizeVariablesToProductImage(images?.[0]?.url);

    return (
        <>
            <ProductDetailsSharedLayout
                productData={productData}
            >
                <ColorSelector colors={colors} activeColorId={productModelColorId} />
                <SizeSelector
                    sizingText={sizingText}
                    sizes={sizes}
                    inputName="pdp-size-selector"
                    selectedVariantId={selectedVariantId}
                    onSelect={handleSizeSelect}
                    isError={sizeError}
                />
                <div className={styles.sizeActions}>
                    {sizeGuideContent && <RawHtml html={sizeGuideContent} />}
                    <Easysize
                        product={product}
                        activeSizeId={selectedVariantId}
                        onSizeFound={handleSizeFound}
                    />
                </div>
                <Actions
                    sizes={sizes}
                    activeSizeId={selectedVariantId}
                    productColorId={productModelColorId}
                    onAddToBasket={addToBasket}
                    onReserve={handleReserve}
                />
            </ProductDetailsSharedLayout>
            <Suspense>
                <Modal
                    headline={modalHeadline}
                    isOpen={isReserveActive}
                    onClose={handleModalClose}
                    variant="compact"
                    scrollLock={true}
                    doNotRemoveContentFromDOMWhenClosed
                >
                    <ReserveContainer
                        key={selectedVariantId}
                        initialVariantId={selectedVariantId}
                        product={product}
                        setIsReserveActive={setIsReserveActive}
                    />
                </Modal>
            </Suspense>
        </>
    );
});

ProductDetails.displayName = 'ProductDetails';
export default ProductDetails;
